import { getErrorState, getSuccessState } from '@automata/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import _usePollWorkflowPlanStatus from './_useWorkflowPlanStatus';
import { Plan, Result, WorkflowPlanData } from './types';
import { useListPlans, usePlanWorkflowStart } from '../apis/wfbApiComponents';

// TODO: (AL, 19/07/24) Need to add a test for this one
export const useWorkflowPlan = (workflowID?: string): WorkflowPlanData => {
  const queryClient = useQueryClient();
  const isWorkflowSaved = workflowID !== undefined;

  const {
    data: startWorkflowPlanData,
    mutateAsync: planWorkflowStartMutate,
    error: planWorkflowStartError,
    status: planWorkflowStartStatus,
  } = usePlanWorkflowStart({
    onSuccess: () => {
      queryClient.invalidateQueries(['v3', 'workflow', workflowID, 'plan']);
    },
  });

  const isPlanMissing = startWorkflowPlanData === undefined;

  const {
    data: listPlansData,
    error: listPlansError,
    fetchStatus: listPlansFetchStatus,
  } = useListPlans(
    {
      pathParams: {
        workflowId: workflowID ?? '',
      },
    },
    {
      staleTime: Infinity,
      enabled: isWorkflowSaved && isPlanMissing,
    }
  );

  const mostRecentPlan: Plan | undefined = useMemo(() => {
    if (startWorkflowPlanData?.id) {
      return {
        id: startWorkflowPlanData.id,
        status: 'PENDING',
      };
    }
    if (listPlansFetchStatus !== 'fetching' && listPlansData?.[0]?.id) {
      return {
        id: listPlansData[0].id,
        // this is needed to patch a bug in the API where plans are always returned as COMPLETED
        // can be removed after resolution of https://automata-tech.atlassian.net/browse/G2-2007
        status: 'PENDING',
      };
    }
    return undefined;
  }, [listPlansData, listPlansFetchStatus, startWorkflowPlanData?.id]);

  const {
    data: pollWorkflowPlanStatusData,
    error: pollWorkflowPlanStatusError,
    fetchStatus: pollWorkflowPlanStatusFetchStatus,
  } = _usePollWorkflowPlanStatus({
    workflowId: workflowID,
    planId:
      mostRecentPlan?.status === 'PENDING' ? mostRecentPlan.id : undefined,
  });

  const makeReturnData = useCallback(
    (result: Result): WorkflowPlanData => ({
      result,
      isIdle: [
        planWorkflowStartStatus,
        pollWorkflowPlanStatusFetchStatus,
        listPlansFetchStatus,
      ].every(
        (status) =>
          status === 'idle' || status === 'error' || status === 'success'
      ),
      requestNewPlan: planWorkflowStartMutate,
    }),
    [
      listPlansFetchStatus,
      planWorkflowStartMutate,
      planWorkflowStartStatus,
      pollWorkflowPlanStatusFetchStatus,
    ]
  );

  if (listPlansError) {
    return makeReturnData(
      getErrorState({
        errorType: 'listWorkflowPlans',
        error: listPlansError,
      })
    );
  }

  if (pollWorkflowPlanStatusError) {
    return makeReturnData(
      getErrorState({
        errorType: 'pollWorkflowPlanStatus',
        error: pollWorkflowPlanStatusError,
      })
    );
  }

  if (planWorkflowStartError) {
    return makeReturnData(
      getErrorState({
        errorType: 'planWorkflowStart',
        error: planWorkflowStartError,
      })
    );
  }

  return makeReturnData(getSuccessState(pollWorkflowPlanStatusData));
};
